import React from 'react';
import { Button, Flex } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { MantineTable } from '@dealstack/frontend/shared/components/Table/MantineTable/MantineTable';
import { useSecurityAllocationsController } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityTabs/SecurityAllocationsTabPanel/useSecurityAllocationsController.ts';
import { SecurityAllocationDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityTabs/SecurityAllocationsTabPanel/SecurityAllocationDrawer/SecurityAllocationDrawer.tsx';
import { ConfirmDeleteDrawer } from '@dealstack/frontend/shared/components/ConfirmDeleteDrawer/ConfirmDeleteDrawer';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { useQuery } from '@tanstack/react-query';
import { getAllocationsAvailableMips } from '@dealstack/frontend/app/services/securityAllocations.service.ts';
import { useParams } from 'react-router-dom';

interface Props {
	hasEditorPermission?: boolean;
}

export const SecurityAllocationsTabPanel = ({ hasEditorPermission }: Props) => {
	const tCapManage = useTranslationCapManage();
	const { dealId, securityId } = useParams();

	const {
		showCreateDrawer,
		setShowCreateDrawer,
		getAllocationsRequest,
		allocationColumns,
		isTableLoading,
		editAllocation,
		setEditAllocation,
		deleteAllocation,
		setDeleteAllocation,
		deleteAllocationRequest,
	} = useSecurityAllocationsController(hasEditorPermission);

	const getAvailableMipsRequest = useQuery({
		queryKey: [
			'',
			{
				securityId: securityId ?? '',
				dealId: dealId ?? '',
				includeMipId: editAllocation?.mipId,
			},
		],
		queryFn: () =>
			getAllocationsAvailableMips({
				securityId: securityId ?? '',
				dealId: dealId ?? '',
				includeMipId: editAllocation?.mipId,
			}),
		enabled: !!(dealId && securityId),
	});

	const mipName = deleteAllocation?.mipName ?? '';

	const handleConfirmDelete = () => {
		if (deleteAllocation) {
			deleteAllocationRequest.mutate(deleteAllocation.id, {
				onSuccess: async () => {
					setDeleteAllocation(null);
					void getAllocationsRequest.refetch();
					void getAvailableMipsRequest.refetch();
					showToastV2({
						message: tCapManage('securities.details.allocations.delete.success'),
					});
				},
			});
		}
	};

	return (
		<>
			{hasEditorPermission && (
				<Flex p="xs" justify="flex-end">
					<Button
						leftSection={<Icon variant="plus" size="lg" />}
						disabled={!getAvailableMipsRequest?.data?.length}
						onClick={() => {
							setShowCreateDrawer(true);
						}}
					>
						{tCapManage('securities.details.allocations.new_allocation')}
					</Button>
				</Flex>
			)}
			<MantineTable
				mantineTableBodyProps={() => ({
					sx: {
						'& .mantine-Text-root': {
							maxWidth: '100%',
						},
						'& tr:last-of-type td': {
							borderBottom: 'none',
						},
					},
				})}
				enableTopToolbar={false}
				columns={allocationColumns}
				isLoading={isTableLoading}
				data={getAllocationsRequest.data ?? []}
				enableRowActions={false}
				enableRowSelection={false}
				enableSearchBar={false}
				enablePagination={false}
				hideBorders
			/>
			<SecurityAllocationDrawer
				opened={showCreateDrawer || !!editAllocation}
				onClose={() => {
					setShowCreateDrawer(false);
					setEditAllocation(null);
				}}
				onSuccess={() => {
					void getAvailableMipsRequest.refetch();
				}}
				allocation={editAllocation}
				mips={getAvailableMipsRequest?.data ?? []}
			/>
			<ConfirmDeleteDrawer
				opened={!!deleteAllocation}
				onClose={() => setDeleteAllocation(null)}
				title={tCapManage('securities.details.allocations.drawer_delete.title')}
				inputLabel={tCapManage('securities.details.allocations.drawer_delete.inputLabel')}
				inputPlaceholder={mipName}
				confirmDeleteText={mipName}
				wordMismatchErrorMessage={tCapManage(
					'securities.details.allocations.drawer_delete.wordMismatch'
				)}
				deleteBtnText={tCapManage('delete.entity.drawer.confirmDelete')}
				cancelBtnText={tCapManage('delete.entity.drawer.cancel')}
				deleteSuccess={deleteAllocationRequest.isSuccess}
				deleteLoading={deleteAllocationRequest.isPending}
				handleDelete={handleConfirmDelete}
			/>
		</>
	);
};
